import { Extension, type Range } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration } from '@tiptap/pm/view';

import { DecorationSet } from '@tiptap/pm/view';
export const inputRegex = /(?:^|\s)((?:==)((?:[^~=]+))(?:==))$/;
export const pasteRegex = /(?:^|\s)((?:==)((?:[^~=]+))(?:==))/g;

export interface SelectionHighlightOptions {
  HTMLAttributes: Record<string, string>;
}

type HighlightType = 'ai-prompt' | 'drag-handle';
type Highlight = Range & { type?: HighlightType };

const key = new PluginKey<{
  pos: Highlight[];
  add?: Highlight[];
  clear?: boolean;
}>('selectionHighlight');

export const selectionHighlightExtension =
  Extension.create<SelectionHighlightOptions>({
    name: 'selectionHighlight',

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key,
          state: {
            init() {
              return {
                pos: [],
              };
            },
            apply(tr, value) {
              const newPluginState = tr.getMeta(key);

              if (newPluginState?.clear) {
                return {
                  pos: [],
                };
              }

              if (newPluginState?.add) {
                return {
                  pos: [...(newPluginState.pos || []), ...newPluginState.add],
                };
              }

              return newPluginState || value;
            },
          },
          props: {
            decorations(state) {
              const data = key.getState(state);
              if (!data || !data.pos) {
                return null;
              }
              return DecorationSet.create(
                state.doc,
                data.pos.map((range) => {
                  const className =
                    range.type === 'drag-handle'
                      ? 'virtual-selection-draggable'
                      : 'virtual-selection';
                  return Decoration.inline(range.from, range.to, {
                    class: className,
                  });
                }),
              );
            },
          },
        }),
      ];
    },

    addCommands() {
      return {
        setSelectionHighlight:
          (type: HighlightType = 'ai-prompt') =>
          ({ editor }) => {
            editor.view.dispatch(
              editor.view.state.tr.setMeta(key, {
                add: [
                  {
                    from: editor.state.selection.from,
                    to: editor.state.selection.to,
                    type,
                  },
                ],
              }),
            );
            return true;
          },
        unsetSelectionHighlight:
          () =>
          ({ editor }) => {
            editor.view.dispatch(
              editor.view.state.tr.setMeta(key, { clear: true }),
            );
            return true;
          },
      };
    },
  });

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    selectionHighlight: {
      /**
       * Set a AIHighlight mark
       */
      setSelectionHighlight: (type?: HighlightType) => ReturnType;
      /**
       * Unset a AIHighlight mark
       */
      unsetSelectionHighlight: () => ReturnType;
    };
  }
}
